import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, CalendarDays, Users, ClipboardCheck, Wallet, TrendingUp, CalendarPlus, FileText,
  MapPin, Database, CreditCard, UserCog, LogOut, Menu, X, UserCircle, Settings, ClipboardList,
} from 'lucide-react';
import { useSession } from '../lib/SessionContext';
import { signOut } from '../lib/auth';
import { confirmLeaving } from '../lib/unsavedChanges';
import { t } from '../i18n';

/**
 * Which sections each role sees. Admin is also a practising professional, so
 * it gets the professional's own tools plus the practice-wide ones.
 */
const navFor = (role) => {
  const professional = [
    { to: '/', label: t('nav.home'), icon: Home, end: true },
    { to: '/agenda', label: t('nav.agenda'), icon: CalendarDays },
    { to: '/reservar', label: t('booking.title'), icon: CalendarDays },
    { to: '/pacientes', label: t('nav.patients'), icon: Users },
    { to: '/historia-clinica', label: t('nav.clinicalHistory'), icon: FileText },
    // Where questionnaires are written; they are sent from the clinical history (0065).
    { to: '/formularios', label: t('nav.forms'), icon: ClipboardList },
    { to: '/solicitudes', label: t('nav.approvals'), icon: ClipboardCheck },
    { to: '/pagos', label: t('nav.payments'), icon: Wallet },
    { to: '/caja', label: t('nav.cashFlow'), icon: TrendingUp },
    // The lists the practice shares; professionals add what is missing. What
    // each one charges is in Configuración.
    { to: '/datos-maestros', label: t('nav.masterData'), icon: Database },
  ];

  // Everything a professional sets, and for admin the practice's settings too.
  // Last in both menus. On a phone, where the header hides the name, this is
  // the only way in.
  const settings = { to: '/configuracion', label: t('nav.settings'), icon: Settings };

  if (role === 'admin') {
    return [
      ...professional,
      { to: '/profesionales', label: t('nav.professionals'), icon: UserCog },
      { to: '/sedes', label: t('nav.locations'), icon: MapPin },
      { to: '/suscripciones', label: t('nav.subscriptions'), icon: CreditCard },
      settings,
    ];
  }

  if (role === 'therapist') return [...professional, settings];

  // Patients get their dashboard, self-booking, and what they owe (0046)
  return [
    { to: '/', label: t('nav.home'), icon: Home, end: true },
    { to: '/reservar', label: t('booking.titlePatient'), icon: CalendarPlus },
    { to: '/mis-pagos', label: t('nav.payments'), icon: Wallet },
    { to: '/mi-perfil', label: t('profile.title'), icon: UserCircle },
  ];
};

const Layout = ({ children }) => {
  const { role, profile } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const items = navFor(role);
  const ownSettings = role === 'patient' ? '/mi-perfil' : '/configuracion';

  // Configuración saves card by card, so a card can be left unsaved. Going to
  // another screen asks first; a link to the screen already open does not.
  const guard = (to) => (event) => {
    if (to !== location.pathname && !confirmLeaving()) event.preventDefault();
  };

  const handleSignOut = async () => {
    if (!confirmLeaving()) return;
    await signOut();
    navigate('/ingresar', { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
      isActive ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-800/60'
    }`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-indigo-900 text-white">
        {/* Full width, unlike the page below: capped at max-w-7xl, admin's
            thirteen sections wrapped to three lines beside empty margins. */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 xl:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="rounded p-1.5 hover:bg-indigo-800 lg:hidden"
              aria-label={t('nav.menu')}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <span className="text-lg font-bold">{t('common.appName')}</span>
          </div>

          <nav className="hidden flex-1 flex-wrap items-center gap-1 lg:flex">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} onClick={guard(item.to)}>
                <item.icon size={16} />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* The name doubles as the way into your own settings: Mi perfil
                for a patient, Configuración for a professional. Hidden on a
                phone, where the menu lists them. */}
            <NavLink
              to={ownSettings}
              onClick={guard(ownSettings)}
              className="hidden rounded-lg px-2 py-1 text-right hover:bg-indigo-800 sm:block"
            >
              <p className="text-sm font-medium leading-tight">
                {profile?.first_name} {profile?.last_name}
              </p>
              <p className="text-xs text-indigo-300">{t(`roles.${role}`)}</p>
            </NavLink>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-800 px-3 py-2 text-sm hover:bg-indigo-700"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t('auth.logout')}</span>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="flex flex-col gap-1 border-t border-indigo-800 px-4 pb-4 pt-2 lg:hidden">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                onClick={(event) => {
                  guard(item.to)(event);
                  if (!event.defaultPrevented) setMenuOpen(false);
                }}
              >
                <item.icon size={16} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
};

export default Layout;
